'use es6';

import createDSAdapter from './createDSAdapter';
import http from 'hub-http/clients/apiClient';
import { Map as ImmutableMap } from 'immutable';
import { AdapterCache } from '../requestCaches/adaptersCache';
import { isUnifiedEventObjectTypeId } from '../../../filterQueryFormat/ObjectTypeId';
import unifiedEventAdapter from './unifiedEventAdapter';
import DSFieldRecord from '../../../filterQueryFormat/DSFieldRecord/DSFieldRecord';
import { getFrameworkBuilderMetaDataValue } from '../../../utilities/frameworkBuilder';
import { getPropertyDefinitionOverrideForUnifiedEventProperty } from './unifiedEventPropertyDefinitionOverrides';
import { OBJECT_COORDINATES } from '../../../filterQueryFormat/UnifiedEventPropertyTypes';
const getDSFieldRecordFromFrameworkBuilderMetaData = (name, metadata) => {
  return DSFieldRecord({
    label: getFrameworkBuilderMetaDataValue('label', metadata) || name,
    type: getFrameworkBuilderMetaDataValue('type', metadata),
    name,
    description: getFrameworkBuilderMetaDataValue('description', metadata),
    numberDisplayHint: getFrameworkBuilderMetaDataValue('numberDisplayHint', metadata),
    referencedObjectType: getFrameworkBuilderMetaDataValue('targetObjectType', metadata)
  });
};
export const fetchedDataCache = new AdapterCache();
export const processedDataCache = new AdapterCache();
export function makeUnifiedEventsAdapter() {
  function isAssetFamilySupported(assetFamily) {
    return isUnifiedEventObjectTypeId(assetFamily);
  }
  function getFetcherFromProps() {
    return http.get;
  }
  function fetch(httpClient, _, assetFamily) {
    const cacheKey = `framework-builder/v1/read/metadata/property/${assetFamily}/filtersFrontEnd`;
    if (fetchedDataCache.get(cacheKey)) {
      return fetchedDataCache.get(cacheKey);
    }
    const request = unifiedEventAdapter.fetch(http.get).then(result => unifiedEventAdapter.updater(result)).then(unifiedEvents => {
      if (unifiedEvents && unifiedEvents.get(assetFamily)) {
        const fullyQualifiedName = unifiedEvents.getIn([assetFamily, 'fullyQualifiedName']);
        return Promise.all([httpClient(`framework-builder/v1/read/metadata/property/${fullyQualifiedName}/filtersFrontEnd`), httpClient(`properties/v4/${fullyQualifiedName}`)]);
      }
      return Promise.reject(new Error(`No fullyQualifiedName found for ${assetFamily}`));
    });
    fetchedDataCache.set(cacheKey, request);
    return request;
  }
  function updater(fetchedData, _, assetFamily) {
    const [frameworkBuilder, propertyDefinitions] = fetchedData;
    const cacheKey = `FRAMEWORK_BUILDER_TYPE_PROPERTIES_${assetFamily}`;
    if (processedDataCache.get(cacheKey)) {
      return processedDataCache.get(cacheKey);
    }
    const formatted = Object.entries(frameworkBuilder).reduce((acc, [key, value]) => {
      const {
        metadata
      } = value;
      const type = getFrameworkBuilderMetaDataValue('type', metadata);
      const propertyDefinition = propertyDefinitions.find(({
        property
      }) => key === property.name);
      if (type !== OBJECT_COORDINATES && propertyDefinition) {
        // if we are using the property definition, we might need to apply a override to get properties to appear as customer expect
        // generally this is to force number or string properties to appear as enumerations
        const propertyDefinitionOverride = getPropertyDefinitionOverrideForUnifiedEventProperty(assetFamily, propertyDefinition.property.name);
        const property = propertyDefinitionOverride ? Object.assign({}, propertyDefinition.property, propertyDefinitionOverride) : propertyDefinition.property;
        return acc.set(key, DSFieldRecord.fromJS(property));
      }
      if (type === OBJECT_COORDINATES && getFrameworkBuilderMetaDataValue('targetObjectType', metadata)) {
        return acc.set(key, getDSFieldRecordFromFrameworkBuilderMetaData(key, metadata));
      }
      return acc;
    }, ImmutableMap());
    processedDataCache.set(cacheKey, formatted);
    return formatted;
  }
  return createDSAdapter({
    fetch,
    getFetcherFromProps,
    isAssetFamilySupported,
    updater
  });
}
const unifiedEventPropertiesAdapter = makeUnifiedEventsAdapter();
export default unifiedEventPropertiesAdapter;